<template>
  <div class="p-grid">
    <div class="p-col-12">
      <div class="card">
        <Toolbar class="p-mb-4 p-p-2">
          <template #start>
            <div style="font-size: 1.4em; color: inherit" class="p-p-2">
              {{ $t('Trial Applications') }}
            </div>
            <div class="p-p-1">
              <AddNewButton
                  :disabled="dataTableIsLoading"
                  :loading="dataTableIsLoading"
                  :label="$t('Add')"
                  @click="addNewItem"/>
            </div>
          </template>

          <template #end>
            <div class="p-d-flex p-ai-center p-mr-2">
              <Checkbox id="tenant-applications-requires-action"
                        v-model="showOnlyRequiresAction"
                        :binary="true"
                        :disabled="dataTableIsLoading"/>
              <label for="tenant-applications-requires-action">
                <div class="pointer p-p-1">
                  {{ $t('requiresActionLabel') }}
                </div>
              </label>
            </div>

            <CustomDropdown v-model="statusFilter"
                            :options="statusOptions"
                            optionLabel="label"
                            optionValue="value"
                            :placeholder="$t('All Statuses')"
                            :showClear="true"
                            :disabled="dataTableIsLoading || showOnlyRequiresAction"
                            class="p-mr-2"
                            style="width: 210px">
              <template #value="slotProps">
                <span v-if="statusFilter !== null"
                      class="state-badge"
                      :class="applicationStatusClass(statusFilter)">
                  {{ applicationStatusLabel(statusFilter) }}
                </span>
                <span v-else>{{ slotProps.placeholder }}</span>
              </template>

              <template #option="slotProps">
                <span class="state-badge"
                      :class="applicationStatusClass(slotProps.option.value)">
                  {{ slotProps.option.label }}
                </span>
              </template>
            </CustomDropdown>

            <SearchInput
                :disabled="dataTableIsLoading"
                @change-processed-search-value="changeProcessedSearchValue"
                v-model="searchData"
                @show-spinner="isLoading = true"/>
          </template>
        </Toolbar>

        <Spinner v-if="isLoading"/>

        <DesktopDataTable
            v-else
            :showHeader="false"
            :dataTableIsLoading="dataTableIsLoading"
            :itemsArrayIsEmpty="itemsArrayIsEmpty"
            :sort="sort"
            :showSearchInput="false"
            :actionButtonsCount="2"
            @sort-items="sortItems"
            :headers="[
                {name: 'id', title: 'ID', sortable: true},
                {name: 'company_name', title: $t('Company'), sortable: false},
                {name: 'website', title: $t('Website'), sortable: false},
                {name: 'applicant_first_name', title: $t('Applicant'), sortable: false},
                {name: 'phone_number', title: $t('Phone'), sortable: false},
                {name: 'email', title: $t('Email'), sortable: false},
                {name: 'email_verified_at', title: $t('Email Confirmed'), sortable: false},
                {name: 'status', title: $t('Status'), sortable: false},
                {name: 'converted_tenant_id', title: $t('Tenant'), sortable: false},
                {name: 'comment', width: '36px', sortable: false},
                {name: 'created_at', title: $t('Created'), sortable: true},
              ]"
          >
          <template v-slot:body>
            <tr v-for="(item, index) of items"
                :key="item.id ?? index"
                :class="{'updated-item': isItemUpdated(item.id)}">
              <td>
                <span v-if="item.id">{{ item.id }}</span>
                <i v-else class="ti-minus"/>
              </td>

              <td>
                <div v-if="item.company_name">
                  <div>{{ item.company_name }}</div>

                  <div class="p-text-secondary table-small-text">
                    <span v-if="item.registry_code">{{ item.registry_code }}</span>
                    <span v-if="item.registry_code && item.country_code"> · </span>
                    <span v-if="item.country_code">{{ item.country_code }}</span>
                  </div>

                </div>

                <i v-else class="ti-minus"/>
              </td>

              <td>
                <span v-if="item.website">{{ item.website }}</span>
                <i v-else class="ti-minus"/>
              </td>

              <td>
                <div v-if="applicantName(item)">
                  <div class="p-d-flex p-ai-center p-flex-wrap">
                    <div>{{ applicantName(item) }}</div>
                    <UserLanguageBadge :language="item.language" showAnyway/>
                  </div>
                  <div v-if="item.applicant_role" class="p-text-secondary table-small-text">
                    {{ applicantRoleLabel(item.applicant_role) }}
                  </div>
                </div>
                <i v-else class="ti-minus"/>
              </td>

              <td>
                <span v-if="item.phone_code && item.phone_number">
                  +{{ item.phone_code }} {{ item.phone_number }}
                </span>
                <i v-else class="ti-minus"/>
              </td>

              <td>
                <span v-if="item.email">{{ item.email }}</span>
                <i v-else class="ti-minus"/>
              </td>

              <td>
                <i v-if="item.email_verified_at"
                   class="pi pi-check green-color"
                   v-tooltip.top="formatDate(item.email_verified_at)"/>
                <i v-else class="pi pi-times warning-color"/>
              </td>

              <td>
                <div class="p-d-flex p-flex-wrap p-ai-center">
                   <div class="state-badge"
                         :class="applicationStatusClass(item.status)">
                  {{ applicationStatusLabel(item.status) }}
                </div>
                </div>

                <div v-if="item.reviewed_at"
                     class="p-text-secondary table-small-text">
<!--                  {{ formatDate(item.reviewed_at) }}-->
                  <span>{{ formatBy(item.reviewed_at, item.reviewedBy) }}</span>
                </div>
                <div v-if="item.onboarding_sent_at"
                     v-tooltip.top="item.onboarding_expires_at
                     ? `${$t('tenantApplicationInvitationExpires')}: ${formatDate(item.onboarding_expires_at)}`
                     : null"
                     class="p-text-secondary table-small-text">
                  {{ $t('tenantApplicationInvitationSent') }}:
                  {{ formatDate(item.onboarding_sent_at) }}
                </div>
              </td>

              <td>
                <span v-if="item.converted_tenant_id">{{ item.converted_tenant_id }}</span>
                <i v-else class="ti-minus"/>
              </td>

              <td>
                <i v-if="item.comment" class="ti-notepad" v-tooltip.top="item.comment"/>
              </td>

              <td class="table-date-line">
                <span v-if="item.created_at">{{ formatBy(item.created_at, item.creator) }}</span>
                <i v-else class="ti-minus"/>
              </td>

              <td>
                <div class="actions">
                  <TenantApplicationActionsButton
                      :application="item"
                      :disabled="dataTableIsLoading"
                      :processing="applicationActionInProgress(item)"
                      @action="handleApplicationAction"/>
                  <EditButton :disabled="dataTableIsLoading"
                              @click="editItem(item)"/>
                </div>
              </td>
            </tr>
          </template>
        </DesktopDataTable>

        <OwnPaginator
            v-show="items && items.length"
            :rows="pagination.perPage"
            :currentPage="pagination.currentPage - 1"
            :totalPages="pagination.pageCount"
            :totalRecords="pagination.totalCount"
            :rowsPerPageOptions="[5,10,25,50,100,200]"
            :pageLinkSize="11"/>

        <TotalRecordsData :pagination="pagination" :itemsArrayIsEmpty="itemsArrayIsEmpty"/>

        <TenantApplicationModal
            :visible="itemModal"
            :item="item"
            @update-items="updateTenantApplication"
            @close="closeItemModal"/>

        <ConfirmDeleteModal
            :visible="confirmItemDeleteModal"
            :modalDataIsLoading="modalDataIsLoading"
            :data="itemToDeleteData"
            @close="closeConfirmItemDeleteModal"
            @delete-item="deleteTenantApplication"/>

        <ConfirmModal
            :visible="applicationActionConfirmModal"
            :text="applicationActionConfirmText"
            :showSpinner="applicationActionIsLoading"
            @confirm="confirmApplicationAction"
            @close="closeApplicationActionConfirmModal"/>

      </div>
    </div>
  </div>
</template>

<script>
import httpClient from '@/services/http.services'
import datatableMixins from '@/mixins/datatableMixins'
import settings from '@/settings'
import formatMixins from '@/mixins/formatMixins'
import showErrorsMixins from '@/mixins/showErrorsMixins'
import TenantApplicationModal from "@/pages/system/components/TenantApplicationModal.vue";
import TenantApplicationActionsButton from "@/pages/system/components/TenantApplicationActionsButton.vue";
import UserLanguageBadge from "@/components/UserLanguageBadge.vue";

export default {
  mixins: [showErrorsMixins, datatableMixins, formatMixins],
  components: {
    TenantApplicationActionsButton,
    TenantApplicationModal,
    UserLanguageBadge,
  },
  data() {
    return {
      applicationActionConfirmModal: false,
      applicationActionConfirmText: '',
      applicationActionProcessingId: null,
      pendingApplicationAction: null,
      fieldsExpand: 'reviewedBy',
      apiLink: 'tenant-application',
      deleteItemUrl: 'tenant-application/delete',
      routePath: '/tenant-applications',
      sort: {
        column: 'created_at',
        direction: '-',
      },
      applicationStatuses: {
        EMAIL_PENDING: 1,
        REVIEW_PENDING: 2,
        NEEDS_INFO: 3,
        APPROVED: 4,
        REJECTED: 5,
        COMPLETED: 6,
        EXPIRED: 9,
      },
    }
  },

  watch: {
    '$store.state.firstLayerIsOpened'(value) {
      if (!value) {
        if (this.applicationActionConfirmModal) {
          this.closeApplicationActionConfirmModal()
        }
      }
    },
  },

  mounted() {
    this.scrollToTop()
    if (settings.autoLogoutTimeout && this.$store.state.logoutTimestampIsOverdue) return false
    this.checkRouteQuery()
    this.getPageData()
  },

  methods: {
    getPageData() {
      this.getItems()
    },

    updateFilterQuery(changes = {}) {
      const query = { ...this.$route.query }

      delete query.page

      Object.entries(changes).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '' || value === false) {
          delete query[key]
        } else {
          query[key] = String(value)
        }
      })

      this.$router.replace({
        path: this.routePath,
        query,
      }).catch(() => {})
    },

    applicantName(item) {
      return [item?.applicant_first_name, item?.applicant_last_name]
          .filter(Boolean)
          .join(' ')
          .trim()
    },

    applicantRoleLabel(role) {
      const labels = {
        owner: this.$t('Owner'),
        board_member: this.$t('Board member'),
        manager: this.$t('Manager'),
        employee: this.$t('Employee'),
        other: this.$t('other'),
      }

      return labels[role] ?? role ?? '-'
    },

    applicationLabel(application) {
      return application?.company_name || `#${application?.id}`
    },

    handleApplicationAction({ action, application }) {
      if (action === 'delete') {
        this.confirmItemDelete(application)
        return
      }

      if (![
        'approve',
        'needs_info',
        'reject',
        'return_to_review',
        'send_onboarding_invitation',
      ].includes(action)) {
        return
      }

      this.openApplicationActionConfirm(application, action)
    },

    openApplicationActionConfirm(application, action) {
      if (
          !application?.id
          || this.applicationActionInProgress(application)
          || this.applicationActionConfirmModal
      ) {
        return
      }

      const actionLabels = {
        approve: this.$t('Approve'),
        needs_info: this.$t('tenantApplicationStatusNeedsInfo'),
        reject: this.$t('Reject'),
        return_to_review: this.$t('Return To Review'),
        send_onboarding_invitation: this.$t(
            application.onboarding_sent_at
                ? 'tenantApplicationResendInvitation'
                : 'tenantApplicationSendInvitation'
        ),
      }

      this.pendingApplicationAction = {
        action,
        application,
      }
      this.applicationActionConfirmText =
          `${actionLabels[action]}: ${this.applicationLabel(application)}?`
      this.applicationActionConfirmModal = true
      this.$store.commit('toggleFirstLayer', true)
    },

    closeApplicationActionConfirmModal() {
      if (this.applicationActionIsLoading) {
        return
      }

      this.applicationActionConfirmModal = false
      this.applicationActionConfirmText = ''
      this.pendingApplicationAction = null
      this.$store.commit('toggleFirstLayer', false)
    },

    async confirmApplicationAction() {
      const pending = this.pendingApplicationAction

      if (!pending?.application?.id || !pending?.action) {
        return
      }

      const success = await this.runApplicationAction(
          pending.application,
          pending.action
      )

      if (success) {
        this.closeApplicationActionConfirmModal()
      }
    },

    async runApplicationAction(application, action) {
      const actionUrls = {
        approve: 'tenant-application/approve',
        needs_info: 'tenant-application/needs-info',
        reject: 'tenant-application/reject',
        return_to_review: 'tenant-application/return-to-review',
        send_onboarding_invitation: 'tenant-application/send-onboarding-invitation',
      }

      if (
          !application?.id
          || this.applicationActionInProgress(application)
          || !actionUrls[action]
      ) {
        return false
      }

      this.applicationActionProcessingId = application.id
      this.dataTableIsLoading = true

      try {
        const { data } = await httpClient.post(
            actionUrls[action],
            { id: application.id }
        )

        if (!data?.success) {
          this.showUnknownError()
          return false
        }

        if (data.pending_tenant_applications_count !== undefined) {
          this.$store.commit(
              'updatePendingTenantApplicationsCount',
              data.pending_tenant_applications_count
          )
        }

        const successDetails = {
          approve: 'Approved',
          needs_info: 'tenantApplicationStatusNeedsInfo',
          reject: 'Rejected',
          return_to_review: 'tenantApplicationStatusReviewPending',
          send_onboarding_invitation: 'tenantApplicationInvitationSent',
        }

        this.$toast.add({
          severity: 'success',
          summary: this.$t('Successful'),
          detail: this.$t(successDetails[action]),
          life: settings.toastLife,
        })

        // await this.getItems()
        // await this.updateItems(application.id)
        this.updateItems(application.id, false)
        await this.getItems()

        return true
      } catch (error) {
        this.showError(error)
        return false
      } finally {
        this.applicationActionProcessingId = null
        this.dataTableIsLoading = false
      }
    },

    applicationActionInProgress(application) {
      return (
          !!application?.id
          && +this.applicationActionProcessingId === +application.id
      )
    },

    applicationStatusLabel(status) {
      const labels = {
        [this.applicationStatuses.EMAIL_PENDING]: this.$t('tenantApplicationStatusEmailPending'),
        [this.applicationStatuses.REVIEW_PENDING]: this.$t('tenantApplicationStatusReviewPending'),
        [this.applicationStatuses.NEEDS_INFO]: this.$t('tenantApplicationStatusNeedsInfo'),
        [this.applicationStatuses.APPROVED]: this.$t('Approved'),
        [this.applicationStatuses.REJECTED]: this.$t('Rejected'),
        [this.applicationStatuses.COMPLETED]: this.$t('tenantApplicationStatusCompleted'),
        [this.applicationStatuses.EXPIRED]: this.$t('Expired'),
      }

      return labels[+status] ?? '-'
    },

    applicationStatusClass(status) {
      const classes = {
        [this.applicationStatuses.EMAIL_PENDING]: 'green',
        [this.applicationStatuses.REVIEW_PENDING]: 'yellow',
        [this.applicationStatuses.NEEDS_INFO]: 'orange',
        [this.applicationStatuses.APPROVED]: 'blue',
        [this.applicationStatuses.REJECTED]: 'red',
        [this.applicationStatuses.COMPLETED]: 'dark-blue',
        [this.applicationStatuses.EXPIRED]: 'black',
      }

      return classes[+status] ?? 'black'
    },

    async getItems() {
      if (!this.userCanGetData || this.fetchInFlight) return

      this.$store.commit('SET_FETCH_IN_FLIGHT', true)
      this.dataTableIsLoading = true

      const ctrl = this.setController?.('tenantApplications')

      try {
        const { status, data, headers } = await httpClient({
          url: this.apiLink,
          params: {
            page: this.pagination.currentPage,
            'per-page': this.pagination.perPage,
            sort: this.sort.direction + this.sort.column,
            search: this.processedSearchValue ?? null,
            status: this.showOnlyRequiresAction ? null : this.statusFilter,
            pending: this.showOnlyRequiresAction ? 1 : 0,
            expand: this.fieldsExpand,
          },
          signal: ctrl?.signal,
        })

        if (status === 200 && data && Array.isArray(data)) {
          const hasPaginationHeaders = !!headers['x-pagination-total-count']

          if (hasPaginationHeaders) {
            this.pagination.totalCount = +headers['x-pagination-total-count']
            this.pagination.pageCount = +headers['x-pagination-page-count']
          } else {
            this.pagination.totalCount = data.length
            this.pagination.pageCount = 1
          }

          this.items = data

          if (!data.length) {
            this.pagination.pageCount = 1
            this.pagination.currentPage = 1
          }
        } else {
          this.items = []
          this.showUnknownError()
        }
      } catch (err) {
        this.showError(err)
      } finally {
        this.$store.commit('SET_FETCH_IN_FLIGHT', false)
        this.dataTableIsLoading = false
        this.isLoading = false

        if (this._abortors?.tenantApplications === ctrl) {
          this._abortors.tenantApplications = null
        }
      }
    },
    async deleteTenantApplication() {
      const res = await this.deleteItem()

      if (res?.success && res.pending_tenant_applications_count !== undefined) {
        this.$store.commit(
            'updatePendingTenantApplicationsCount',
            res.pending_tenant_applications_count
        )
      }
    },
    updateTenantApplication(data) {
      this.updateItems(data?.id ?? null)

      if (data?.pending_tenant_applications_count !== undefined) {
        this.$store.commit(
            'updatePendingTenantApplicationsCount',
            data.pending_tenant_applications_count
        )
      }
    },
  },

  computed: {
    applicationActionIsLoading() {
      return (
          this.applicationActionConfirmModal
          && this.applicationActionProcessingId !== null
      )
    },

    itemToDeleteData() {
      return this.itemToDelete?.company_name ?? null
    },
    showOnlyRequiresAction: {
      get() {
        return this.$route.query.pending === '1' || this.$route.query.pending === 'true'
      },
      set(value) {
        const changes = {
          pending: value ? 1 : null,
        }

        if (value) {
          changes.status = null
        }

        this.updateFilterQuery(changes)
      },
    },

    statusFilter: {
      get() {
        if (this.showOnlyRequiresAction) return null

        const status = Number(this.$route.query.status)
        const allowed = Object.values(this.applicationStatuses)

        return allowed.includes(status) ? status : null
      },
      set(value) {
        this.updateFilterQuery({
          status: value ?? null,
          pending: null,
        })
      },
    },

    statusOptions() {
      return [
        { label: this.$t('tenantApplicationStatusEmailPending'), value: this.applicationStatuses.EMAIL_PENDING },
        { label: this.$t('tenantApplicationStatusReviewPending'), value: this.applicationStatuses.REVIEW_PENDING },
        { label: this.$t('tenantApplicationStatusNeedsInfo'), value: this.applicationStatuses.NEEDS_INFO },
        { label: this.$t('Approved'), value: this.applicationStatuses.APPROVED },
        { label: this.$t('Rejected'), value: this.applicationStatuses.REJECTED },
        {label: this.$t('tenantApplicationStatusCompleted'), value: this.applicationStatuses.COMPLETED},
        { label: this.$t('Expired'), value: this.applicationStatuses.EXPIRED },
      ]
    },

    itemsArrayIsEmpty() {
      return !this.items?.length
    },
  },
}
</script>

<style scoped lang="scss">
</style>
